import React, {useEffect, useState} from 'react';

import {Link} from 'react-router';
import styled from 'styled-components';
import {v4} from 'uuid';

import {
  OptionalClassNameProps,
  StyledCompactItemHeaderSpan,
} from 'app/general/bootstrap/utils';
import {DocumentAlertBox} from 'app/general/structures/document-alerts/DocumentAlertBox';
import {DocumentIconWithNotifications} from 'app/notifly/components/DocumentIconWithNotifications';
import {useWsOpenaiMessage} from 'app/openai/hooks';
import {AI_FULL_PROCEDURE_PROMPT, AI_POLICY_PROMPT} from 'app/openai/utils';
import {useCreatePolicy, useUpdatePolicyVersion} from 'app/policies/api';
import {
  useCreateProcedure,
  useUpdateProcedureVersion,
} from 'app/procedures/api';
import {generateNewContentStep} from 'app/procedures/utils';
import {gt} from 'app/utils/i18n';
import {markAsSafe} from 'app/utils/strings';

import type {
  DetailPolicySerializedType,
  DetailPolicyVersionSerializedType,
  ProcedureContentStepType,
  ProcedureSerializedType,
  ProcedureVersionSerializedType,
} from 'sweet-types';

function useContentType(
  content_type: 'procedure' | 'policy'
): [
  (
    name: string
  ) => Promise<ProcedureSerializedType> | Promise<DetailPolicySerializedType>,
  (
    url: string,
    name: string,
    content: string,
    steps: Array<ProcedureContentStepType>
  ) =>
    | Promise<ProcedureVersionSerializedType>
    | Promise<DetailPolicyVersionSerializedType>,
  string
] {
  const [createPolicy] = useCreatePolicy();
  const [createProcedure] = useCreateProcedure();

  const [updatePolicyVersion] = useUpdatePolicyVersion();
  const [updateProcedureVersion] = useUpdateProcedureVersion();

  const createDocument = (name: string) => {
    const createDocumentFunction =
      content_type === 'policy' ? createPolicy : createProcedure;

    return createDocumentFunction({
      name,
      teams: [],
      folders: [],
    }).unwrap();
  };

  const updateDocument = (
    url: string,
    name: string,
    content: string,
    steps: Array<ProcedureContentStepType>
  ) => {
    const updateDocumentFunction =
      content_type === 'policy'
        ? updatePolicyVersion({
            url,
            name,
            content,
          })
        : updateProcedureVersion({
            url,
            name,
            description: {
              content,
              files: [],
              embeds: [],
              images: [],
            },
            content: steps,
          });

    return updateDocumentFunction.unwrap();
  };

  return [
    createDocument,
    updateDocument,
    content_type === 'policy' ? AI_POLICY_PROMPT : AI_FULL_PROCEDURE_PROMPT,
  ];
}

type Props = {
  documentName: string;
  content_type: 'procedure' | 'policy';
};

export const NewDocumentOnlineGeneration = ({
  documentName,
  content_type,
}: Props) => {
  const [threadId] = useState(v4());
  const [isError, setIsError] = useState<boolean>(false);
  const [document, setDocument] = useState<
    null | ProcedureSerializedType | DetailPolicySerializedType
  >(null);

  const [createDocument, updateDocumentVersion, aiInstructions] =
    useContentType(content_type);

  // Common data values
  const [content, setContent] = useState('');
  const [name, setName] = useState(documentName);

  // Procedure data values
  const [steps, setSteps] = useState<Array<ProcedureContentStepType>>([]);

  const resetComponentState = () => {
    setIsError(false);
    setContent('');
    setName(documentName);
    setSteps([]);
    setDocument(null);
  };

  function onError() {
    setIsError(true);
  }

  const {
    sendMessage,
    isLoading,
    isError: isAIError,
    commands,
    isOpen,
  } = useWsOpenaiMessage({
    type: `edit_${content_type}_chat`,
    thread_id: threadId,
  });

  useEffect(() => {
    if (document != null && isLoading) {
      if (!isAIError) {
        resetComponentState();
      }
    }
  }, [isAIError]);

  const generateDocumentInChat = () => {
    return createDocument(documentName).then(
      (document: ProcedureSerializedType | DetailPolicySerializedType) => {
        sendMessage(aiInstructions, document.id, document.content_type);
        setDocument(document);
      },
      onError
    );
  };

  useEffect(() => {
    let currentSteps: Array<ProcedureContentStepType> = [];
    commands.forEach((command) => {
      switch (command.command) {
        case 'add_actions': {
          const {name: title, content} = command;
          const step = generateNewContentStep(title, {
            content: content || '',
          });
          currentSteps.push(step);
          setSteps(currentSteps);
          break;
        }
        case 'actions': {
          if ('content' in command) {
            setContent(command.content);
          } else if ('name' in command) {
            setName(command.name);
          } else if ('description' in command) {
            setContent(command.description);
          }
          break;
        }
      }
    });
  }, [commands]);

  useEffect(() => {
    // isLoading is null in the beginning, and we want it to be ``false``.
    if (isLoading === false) {
      updateDocumentVersion(
        document!.current_version.url,
        name,
        content,
        steps
      ).then(() => Promise.resolve(true), onError);
    }
  }, [isLoading]);

  return (
    <li className="list-group-item list-group-item-action flex-column align-items-start">
      <div className="d-flex w-100 justify-content-between">
        <Link
          to={document?.html_url || ''}
          className={`btn ${document == null ? 'disabled' : ''}`}
        >
          <StyledCompactItemHeaderSpan>
            <span className={document == null ? 'font-weight-normal' : ''}>
              <DocumentIconWithNotifications
                contentType={content_type}
                notifications={undefined}
              />
              {name}
            </span>
          </StyledCompactItemHeaderSpan>
        </Link>

        <div className="col-auto order-lg-5">
          <button
            className="btn btn-link"
            disabled={document != null || !isOpen}
            onClick={generateDocumentInChat}
          >
            {document == null ? (
              <span>
                <i className="fa fa-plus mr-2" />
                {gt('Add to my account')}
              </span>
            ) : isLoading && !(isError || isAIError) ? (
              <span>
                <i className="fa fa-circle-notch fa-spin mr-1" />{' '}
                {gt('Writing %(doc)s...', {
                  doc: content_type,
                })}
              </span>
            ) : (
              <span
                className={
                  isLoading && (isError || isAIError)
                    ? 'text-danger'
                    : 'text-muted'
                }
              >
                {' '}
                {isLoading && (isError || isAIError)
                  ? gt('Blank %(doc)s created.', {doc: content_type})
                  : gt('%(icon)s complete', {
                      icon: <i className="fa fa-check-square mr-1" />,
                    })}
              </span>
            )}
          </button>
        </div>
      </div>
      {document != null ? (
        <StyledContentScrollbar className="d-flex mt-2 overflow-auto flex-column-reverse">
          {isLoading && (isError || isAIError) ? (
            <div className="m-2">
              <DocumentAlertBox
                type={'warning'}
                icon="fa-robot"
                title={gt(
                  'Generation for this document stopped because of connection' +
                    ' problems.'
                )}
              />
            </div>
          ) : null}
          <StyledStepContent className="pl-4">
            <>
              {steps.map((step, index) => {
                return (
                  <div key={step.title}>
                    <h3>
                      {index + 1}. {step.title}
                    </h3>
                  </div>
                );
              })}
            </>
          </StyledStepContent>
          <StyledContent
            className="pl-4"
            dangerouslySetInnerHTML={markAsSafe(content)}
          />
        </StyledContentScrollbar>
      ) : null}
    </li>
  );
};

const StyledContent = styled.div`
  h3 {
    font-size: 1rem;
  }
  h4 {
    font-size: 0.9rem;
  }
  ul,
  ol {
    margin-bottom: 1rem;
  }
`;

const StyledContentScrollbar = styled.div.attrs<OptionalClassNameProps>({
  className: 'text-muted small',
})`
  max-height: 200px;
`;

const StyledStepContent = styled.div`
  h3 {
    font-size: 1rem;
  }
  h4 {
    font-size: 0.9rem;
  }
  h5 {
    font-size: 0.8rem;
  }
`;
